import React, { memo, useContext } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import Currency, { formatCurrency } from '@prisjakt/ui-currency';
import Graph from 'src/components/graph';
import PriceHistoryCurve from 'src/components/graph/components/price-history-curve';
import * as S from './styles';
import { ProductPageQuery, ProductStatistics } from 'src/types';
import { formatDate } from '../utils';
import { IGraphItem, IGraphItemRenderProps } from 'src/components/graph/types';

const PriceHistorySummary: React.FC<IPriceHistorySummaryProps> = props => {
  const { t, market } = useContext(AppContext);
  const { product, isLoading } = props;

  if (isLoading || !product?.statistics?.nodes) {
    return <S.PriceHistorySummary />;
  }

  const lowestPriceNodes = product.statistics.nodes
    .filter(Boolean)
    .filter(isFromLastSixMonths) as ProductStatistics[];

  const graphs: IGraphItem<IGraphItemRenderProps>[] = [
    {
      name: 'price-history',
      legend: {
        label: t('product', 'price-history.actual-price'),
        color: '#4895B6',
        lineType: 'normal',
      },
      render: (graphProps: IGraphItemRenderProps) => (
        <PriceHistoryCurve
          key={graphProps.name}
          data={lowestPriceNodes}
          {...graphProps}
        />
      ),
    },
  ];

  const xScaleData = lowestPriceNodes
    .filter(p => p.date)
    .map(({ date }) => new Date(date!));
  const yScaleData = lowestPriceNodes
    .filter(p => p.lowestPrice)
    .map(({ lowestPrice }) => lowestPrice!);

  return (
    <S.PriceHistorySummary>
      <S.FadingBox>
        <S.PriceHistoryHeading>
          {t('product', 'price-history.title')}
        </S.PriceHistoryHeading>
        <S.CurrentPrice>
          {t('product', 'price-history.current-lowest-price', {
            lowestPrice: (
              <Currency
                key="lowestPrice"
                price={
                  product.priceSummary.regular ||
                  product.priceSummary.alternative
                }
              />
            ),
          })}
        </S.CurrentPrice>
        <S.PriceHistoryWrapper>
          {IS_BROWSER && (
            <>
              <Graph
                width={310}
                aspectRatio={2.5}
                showXGrid={false}
                showYGrid={false}
                showXAxis={false}
                showYAxis={false}
                showLegend={false}
                xTickFormat={formatDate}
                yTickFormat={(value: number) =>
                  formatCurrency(value, market.code, false, market.currency!) ||
                  value.toString()
                }
                renderTooltip={tooltip => (
                  <>
                    <div style={{ fontWeight: 'bold', marginBottom: 5 }}>
                      {tooltip.xValue}
                    </div>
                    <div>
                      <Currency price={tooltip.yValue} market={market.code} />
                    </div>
                  </>
                )}
                xScaleData={xScaleData}
                yScaleData={yScaleData}
                graphs={graphs}
                nodes={[
                  product.statistics.nodes
                    .filter(Boolean)
                    .filter(isFromLastSixMonths)
                    .map(item => ({
                      date: item?.date,
                      value: item?.lowestPrice,
                    })),
                ]}
              />
            </>
          )}
        </S.PriceHistoryWrapper>
      </S.FadingBox>
    </S.PriceHistorySummary>
  );
};

const sixMonthsAgo = (() => {
  const date = new Date();
  date.setMonth(date.getMonth() - 6);
  return date.valueOf();
})();

const isFromLastSixMonths = (node: ProductStatistics | null) => {
  if (!node?.date) {
    return false;
  }
  const [year, month, day] = node.date.split('-');
  return (
    new Date(Number(year), Number(month) - 1, Number(day)).valueOf() >
    sixMonthsAgo
  );
};

interface IPriceHistorySummaryProps {
  product: ProductPageQuery['product'];
  isLoading: boolean;
}

export default memo(PriceHistorySummary);
